import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Pagination as MuiPagination,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import InfoIcon from '@mui/icons-material/Info';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { TableSkeleton } from '../components/shared/skeletons';
import { IListPublicationsRequest } from '../../model/base/request/list-publications-request';
import { usePublicationsController } from './context/publications-controller';
import { usePublicationsState } from './context/publications-context';
import { useNavigate, useParams } from 'react-router-dom';

export function PublicationsComponent() {
  const navigate = useNavigate();
  const { categoryId: categoryIdParam, subcategoryId: subcategoryIdParam } = useParams<{ categoryId?: string; subcategoryId?: string }>();
  const { loadData, loadCategories, loadSubcategories, clearSubcategories } = usePublicationsController();
  const { publications, categories, subcategories, loading, loadingFilters } = usePublicationsState();
  const [currentPage, setCurrentPage] = useState(1);

  // Converte params para números se existirem
  const categoryIdFromUrl = categoryIdParam ? parseInt(categoryIdParam, 10) : undefined;
  const subcategoryIdFromUrl = subcategoryIdParam ? parseInt(subcategoryIdParam, 10) : undefined;

  const [filters, setFilters] = useState<IListPublicationsRequest>({
    page: 0,
    pageSize: 12,
    title: '',
    categoryId: categoryIdFromUrl,
    subcategoryId: subcategoryIdFromUrl,
    startDate: '',
    endDate: '',
  });

  // Carregar categorias ao montar o componente
  useEffect(() => {
    loadCategories();
  }, []);

  // Carregar subcategorias se categoryId estiver na URL
  useEffect(() => {
    if (categoryIdFromUrl) {
      loadSubcategories(categoryIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdFromUrl]);

  // Carregar dados quando os parâmetros da URL mudarem
  useEffect(() => {
    const updatedFilters = {
      ...filters,
      categoryId: categoryIdFromUrl,
      subcategoryId: subcategoryIdFromUrl,
      page: 0,
    };
    setFilters(updatedFilters);
    setCurrentPage(1);
    loadData(updatedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdParam, subcategoryIdParam]);

  // Opções de filtro dinâmicas
  const categoryOptions = categories
    .filter(cat => cat.id !== undefined)
    .map(cat => ({
      value: cat.id!,
      label: cat.description || `Categoria ${cat.id}`
    }));

  const subcategoryOptions = subcategories
    .filter(subcat => subcat.id !== undefined)
    .map(subcat => ({
      value: subcat.id!,
      label: subcat.description || `Subcategoria ${subcat.id}`
    }));

  const filterFields: FilterField[] = [
    {
      name: 'title',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por descrição',
      fullWidth: false,
    },
    {
      name: 'categoryId',
      label: 'Categoria',
      type: 'select',
      options: categoryOptions,
      placeholder: 'Selecione uma categoria',
    },
    {
      name: 'subcategoryId',
      label: 'Subcategoria',
      type: 'select',
      options: subcategoryOptions,
      placeholder: 'Selecione uma subcategoria',
      disabled: !filters.categoryId || subcategoryOptions.length === 0,
    },
    {
      name: 'startDate',
      label: 'Data Inicial',
      type: 'date',
    },
    {
      name: 'endDate',
      label: 'Data Final',
      type: 'date',
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [name]: value,
      };

      // Se a categoria mudou, limpar subcategoria e carregar novas subcategorias
      if (name === 'categoryId') {
        newFilters.subcategoryId = undefined;

        if (value) {
          loadSubcategories(value);
        } else {
          clearSubcategories();
        }
      }

      return newFilters;
    });
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, page: 0 };
    setCurrentPage(0);
    setFilters(searchFilters);
    loadData(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListPublicationsRequest = {
      page: 0,
      pageSize: 12,
      title: '',
      categoryId: undefined,
      subcategoryId: undefined,
      startDate: '',
      endDate: '',
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    clearSubcategories();

    // Se houver filtros de categoria/subcategoria na URL, redirecionar para a rota sem filtros
    if (categoryIdParam || subcategoryIdParam) {
      navigate('/publicacoes');
    } else {
      loadData(clearedFilters);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadData(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleViewDocument = (url?: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleDownloadDocument = (url?: string, description?: string) => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `publicacao-${description || 'documento'}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Obter nomes da categoria e subcategoria selecionadas
  const selectedCategory = categories.find(cat => cat.id === categoryIdFromUrl);
  const selectedSubcategory = subcategories.find(subcat => subcat.id === subcategoryIdFromUrl);

  // Construir título e subtítulo dinâmicos
  const getPageTitle = () => {
    if (selectedSubcategory && selectedCategory) {
      return `Publicações - ${selectedCategory.description} > ${selectedSubcategory.description}`;
    }
    if (selectedCategory) {
      return `Publicações - ${selectedCategory.description}`;
    }
    return "Publicações do Diário Oficial";
  };

  const getPageSubtitle = () => {
    if (selectedSubcategory && selectedCategory) {
      return `Consulte as publicações da categoria "${selectedCategory.description}" na subcategoria "${selectedSubcategory.description}"`;
    }
    if (selectedCategory) {
      return `Consulte as publicações da categoria "${selectedCategory.description}"`;
    }
    return "Consulte as publicações do município";
  };

  const getBreadcrumbs = () => {
    const breadcrumbs: Array<{ label: string; href?: string }> = [
      { label: 'Publicações do Diário Oficial', href: '/publicacoes' }
    ];

    if (selectedCategory) {
      breadcrumbs.push({ label: selectedCategory.description || 'Categoria' });
    }

    if (selectedSubcategory) {
      breadcrumbs.push({ label: selectedSubcategory.description || 'Subcategoria' });
    }

    return breadcrumbs;
  };

  return (
    <ListPageLayout
      title={getPageTitle()}
      subtitle={getPageSubtitle()}
      breadcrumbs={getBreadcrumbs()}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListPublicationsRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading || loadingFilters}
        />
      }
    >
      {loading && !publications.hasItems() ? (
        <TableSkeleton rows={12} columns={5} hasActions={true} />
      ) : !publications.hasItems() ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma publicação encontrada com os filtros aplicados.
        </Alert>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: 'primary.main',
                    }}
                  >
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '60px',
                      }}
                    >
                      <ArticleIcon sx={{ verticalAlign: 'middle' }} />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Data de Publicação
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}
                    >
                      Descrição
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Categoria
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Subcategoria
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '150px',
                        textAlign: 'center',
                      }}
                    >
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publications.items.map((publication) => (
                    <TableRow
                      key={publication.id}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'action.hover',
                        },
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PictureAsPdfIcon
                            sx={{
                              fontSize: 32,
                              color: 'error.main',
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(publication.publicationDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5,
                          }}
                        >
                          {publication.description || '-'}
                        </Typography>
                        {publication.isRepublication && (
                          <Chip
                            label="Republicação"
                            size="small"
                            color="warning"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={publication.category || '-'}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {publication.subCategory || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'center',
                          }}
                        >
                          <Tooltip title="Ver Detalhes" arrow>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => navigate(`/publicacoes/detalhes/${publication.id}`)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'info.main',
                                  color: 'white',
                                },
                              }}
                            >
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Visualizar PDF" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDocument(publication.fileViewUrl)}
                              disabled={!publication.fileViewUrl}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                },
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Baixar PDF" arrow>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleDownloadDocument(publication.fileDownloadUrl, publication.description)}
                              disabled={!publication.fileDownloadUrl}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'success.main',
                                  color: 'white',
                                },
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {publications.pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <MuiPagination
                count={publications.pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </ListPageLayout>
  );
}
